import React from "react";
import { Row, Col, Form, Tabs, Tab, Alert as BsAlert } from "react-bootstrap";
import DocumentiPanel from "../DocumentiPanel/DocumentiPanel";
import SediPanel from "../SediPanel/SediPanel";
import LicenzePanel from "../LicenzePanel/LicenzePanel";
import DotazioniPanel from "../DotazioniPanel/DotazioniPanel";
import ValutazioniPanel from "../ValutazioniPanel/ValutazioniPanel";
import NominePanel from "../NominePanel/NominePanel";
import AppaltiPanel from "../AppaltiPanel/AppaltiPanel";
import InfortuniPanel from "../InfortuniPanel/InfortuniPanel";
import RiunioniPanel from "../RiunioniPanel/RiunioniPanel";
import SopralluoghiPanel from "../SopralluoghiPanel/SopralluoghiPanel";
import DpiPanel from "../DpiPanel/DpiPanel";
import ParticolariPanel from "../ParticolariPanel/ParticolariPanel";
import IspettivaPanel from "../IspettivaPanel/IspettivaPanel";
import AttrezzaturePanel from "../AttrezzaturePanel/AttrezzaturePanel";

/**
 * Section
 * Box di raggruppamento con intestazione colorata, per separare visivamente
 * gli "ambiti" della scheda (Anagrafica, Sede, Contatti, Figure…).
 */
function Section({ icon, title, subtitle, children }) {
    return (
        <div className="border rounded-3 mb-3" style={{ overflow: "hidden" }}>
            <div
                className="px-3 py-2 d-flex align-items-center"
                style={{ background: "#eef2f7", borderBottom: "1px solid #dde3ea" }}
            >
                <i className={`bi ${icon} me-2 text-primary`} />
                <span className="fw-semibold text-dark">{title}</span>
                {subtitle ? <span className="text-muted small ms-2">· {subtitle}</span> : null}
            </div>
            <div className="p-3" style={{ background: "#fafbfc" }}>
                {children}
            </div>
        </div>
    );
}

/**
 * AziendaScheda
 * Scheda completa di un'azienda a tab (Dati azienda / Documenti / Attrezzature),
 * riutilizzabile sia inline in pagina (vista agenzia mono-azienda) sia dentro
 * una modale (vista manager multi-società).
 *
 * Props:
 *  - editing: oggetto azienda in editing
 *  - onChange: handler input (name/value/checkbox)
 *  - settori: elenco comparti disponibili
 *  - selectedSettori: array di codici comparto selezionati
 *  - onToggleSettore: (codice) => void
 *  - activeTab / onTabChange: controllo del tab attivo
 */
export default function AziendaScheda({
    editing,
    onChange,
    settori = [],
    selectedSettori = [],
    onToggleSettore,
    activeTab,
    onTabChange,
}) {
    return (
        <Tabs
            activeKey={activeTab}
            onSelect={(k) => onTabChange(k || "dati")}
            className="mb-3"
            mountOnEnter
        >
            <Tab eventKey="dati" title={<span><i className="bi bi-card-text me-1" />Dati azienda</span>}>
                <Section icon="bi-card-list" title="Anagrafica" subtitle="Dati identificativi e comparti di attività">
                <Row className="g-2">
                    <Col xs={12} md={8}>
                        <Form.Label className="small">Ragione sociale *</Form.Label>
                        <Form.Control name="ragione_sociale" value={editing.ragione_sociale || ""} onChange={onChange} />
                    </Col>
                    <Col xs={12} md={4}>
                        <Form.Label className="small">Classe rischio</Form.Label>
                        <Form.Select name="classe_rischio" value={editing.classe_rischio || ""} onChange={onChange}>
                            <option value="">— Seleziona —</option>
                            <option value="BASSO">Basso</option>
                            <option value="MEDIO">Medio</option>
                            <option value="ALTO">Alto</option>
                        </Form.Select>
                    </Col>
                    <Col xs={12} md={4}>
                        <Form.Label className="small">P. IVA</Form.Label>
                        <Form.Control name="partita_iva" value={editing.partita_iva || ""} onChange={onChange} />
                    </Col>
                    <Col xs={12} md={4}>
                        <Form.Label className="small">Codice fiscale</Form.Label>
                        <Form.Control name="codice_fiscale" value={editing.codice_fiscale || ""} onChange={onChange} />
                    </Col>
                    <Col xs={12} md={4}>
                        <Form.Label className="small">Codice ATECO</Form.Label>
                        <Form.Control name="codice_ateco" value={editing.codice_ateco || ""} onChange={onChange} />
                    </Col>
                    <Col xs={12} md={8}>
                        <Form.Label className="small">Comparti / settori di attività *</Form.Label>
                        <div className="border rounded p-2" style={{ background: "#ffffff" }}>
                            <Row className="g-1">
                                {(settori.length ? settori : [{ codice: "GENERICO", denominazione: "Generico / altri settori" }]).map(s => (
                                    <Col xs={12} md={6} key={s.codice}>
                                        <Form.Check
                                            type="checkbox"
                                            id={`settore-${s.codice}`}
                                            label={<span title={s.descrizione || ""}>{s.denominazione}</span>}
                                            checked={selectedSettori.includes(s.codice)}
                                            onChange={() => onToggleSettore(s.codice)}
                                        />
                                    </Col>
                                ))}
                            </Row>
                        </div>
                        <Form.Text className="text-muted">
                            Seleziona <strong>tutti</strong> i comparti dell'azienda (es. vigilanza + investigazioni +
                            sicurezza sussidiaria). Determinano cataloghi e adempimenti proposti.
                        </Form.Text>
                    </Col>
                    <Col xs={6} md={4}>
                        <Form.Label className="small">N. dipendenti</Form.Label>
                        <Form.Control type="number" min="0" name="numero_dipendenti" value={editing.numero_dipendenti ?? 0} onChange={onChange} />
                    </Col>
                    <Col xs={12} md={8}>
                        <Form.Label className="small">Settore (descrizione libera)</Form.Label>
                        <Form.Control name="settore" value={editing.settore || ""} onChange={onChange} />
                    </Col>
                </Row>
                </Section>

                <Section icon="bi-geo-alt" title="Sede legale">
                <Row className="g-2">
                    <Col xs={12} md={8}>
                        <Form.Label className="small">Indirizzo</Form.Label>
                        <Form.Control name="indirizzo_sede_legale" value={editing.indirizzo_sede_legale || ""} onChange={onChange} />
                    </Col>
                    <Col xs={4} md={2}>
                        <Form.Label className="small">CAP</Form.Label>
                        <Form.Control name="cap_sede_legale" value={editing.cap_sede_legale || ""} onChange={onChange} />
                    </Col>
                    <Col xs={8} md={4}>
                        <Form.Label className="small">Città</Form.Label>
                        <Form.Control name="citta_sede_legale" value={editing.citta_sede_legale || ""} onChange={onChange} />
                    </Col>
                    <Col xs={4} md={2}>
                        <Form.Label className="small">Provincia</Form.Label>
                        <Form.Control name="provincia_sede_legale" value={editing.provincia_sede_legale || ""} onChange={onChange} maxLength={2} />
                    </Col>
                    <Col xs={8} md={4}>
                        <Form.Label className="small">Nazione</Form.Label>
                        <Form.Control name="nazione_sede_legale" value={editing.nazione_sede_legale || ""} onChange={onChange} />
                    </Col>
                </Row>
                </Section>

                <Section icon="bi-telephone" title="Contatti">
                <Row className="g-2">
                    <Col xs={12} md={4}>
                        <Form.Label className="small">Telefono</Form.Label>
                        <Form.Control name="telefono" value={editing.telefono || ""} onChange={onChange} />
                    </Col>
                    <Col xs={12} md={4}>
                        <Form.Label className="small">Email</Form.Label>
                        <Form.Control name="email" type="email" value={editing.email || ""} onChange={onChange} />
                    </Col>
                    <Col xs={12} md={4}>
                        <Form.Label className="small">PEC</Form.Label>
                        <Form.Control name="pec" type="email" value={editing.pec || ""} onChange={onChange} />
                    </Col>
                    <Col xs={12} md={4}>
                        <Form.Label className="small">Referente</Form.Label>
                        <Form.Control name="referente_nome" value={editing.referente_nome || ""} onChange={onChange} />
                    </Col>
                    <Col xs={12} md={4}>
                        <Form.Label className="small">Email referente</Form.Label>
                        <Form.Control name="referente_email" value={editing.referente_email || ""} onChange={onChange} />
                    </Col>
                    <Col xs={12} md={4}>
                        <Form.Label className="small">Telefono referente</Form.Label>
                        <Form.Control name="referente_telefono" value={editing.referente_telefono || ""} onChange={onChange} />
                    </Col>
                </Row>
                </Section>

                <Section icon="bi-people" title="Figure D.Lgs. 81/2008" subtitle="Ruoli e nomine della sicurezza">
                <Row className="g-2">
                    <Col xs={12} md={4}>
                        <Form.Label className="small">Datore di lavoro</Form.Label>
                        <Form.Control name="datore_lavoro_nome" value={editing.datore_lavoro_nome || ""} onChange={onChange} />
                    </Col>
                    <Col xs={12} md={4}>
                        <Form.Label className="small">RSPP</Form.Label>
                        <Form.Control name="rspp_nome" value={editing.rspp_nome || ""} onChange={onChange} />
                    </Col>
                    <Col xs={12} md={4} className="d-flex align-items-end">
                        <Form.Check
                            type="checkbox"
                            label="RSPP esterno"
                            name="rspp_esterno"
                            checked={Number(editing.rspp_esterno) === 1}
                            onChange={onChange}
                        />
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Label className="small">Medico competente</Form.Label>
                        <Form.Control name="medico_competente_nome" value={editing.medico_competente_nome || ""} onChange={onChange} />
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Label className="small">Email medico competente</Form.Label>
                        <Form.Control name="medico_competente_email" value={editing.medico_competente_email || ""} onChange={onChange} />
                    </Col>
                    <Col xs={12} md={4}>
                        <Form.Label className="small">RLS</Form.Label>
                        <Form.Control name="rls_nome" value={editing.rls_nome || ""} onChange={onChange} />
                    </Col>
                    <Col xs={12} md={4}>
                        <Form.Label className="small">Addetto antincendio</Form.Label>
                        <Form.Control name="addetto_antincendio" value={editing.addetto_antincendio || ""} onChange={onChange} />
                    </Col>
                    <Col xs={12} md={4}>
                        <Form.Label className="small">Addetto primo soccorso</Form.Label>
                        <Form.Control name="addetto_primo_soccorso" value={editing.addetto_primo_soccorso || ""} onChange={onChange} />
                    </Col>
                </Row>
                </Section>

                <Section icon="bi-journal-text" title="Note e stato">
                <Row className="g-2">
                    <Col xs={12}>
                        <Form.Label className="small">Note</Form.Label>
                        <Form.Control as="textarea" rows={2} name="note" value={editing.note || ""} onChange={onChange} />
                    </Col>
                    <Col xs={12}>
                        <Form.Check
                            type="checkbox"
                            label="Azienda attiva"
                            name="attiva"
                            checked={Number(editing.attiva) === 1}
                            onChange={onChange}
                        />
                    </Col>
                </Row>
                </Section>
            </Tab>

            <Tab eventKey="documenti" title={<span><i className="bi bi-folder2-open me-1" />Documenti</span>}>
                {editing.id_azienda ? (
                    <DocumentiPanel ambito="AZIENDA" idAzienda={editing.id_azienda} settore={editing.settori_codici || editing.settore_codice} />
                ) : (
                    <BsAlert variant="info" className="py-2 mb-0">
                        <i className="bi bi-info-circle me-1" />
                        Salva prima i dati dell'azienda per gestire i documenti (DVR, nomine, piano emergenza…).
                    </BsAlert>
                )}
            </Tab>

            <Tab eventKey="sedi" title={<span><i className="bi bi-geo-alt me-1" />Sedi operative</span>}>
                {editing.id_azienda ? (
                    <SediPanel idAzienda={editing.id_azienda} />
                ) : (
                    <BsAlert variant="info" className="py-2 mb-0">
                        <i className="bi bi-info-circle me-1" />
                        Salva prima i dati dell'azienda per gestire le sedi operative.
                    </BsAlert>
                )}
            </Tab>

            <Tab eventKey="nomine" title={<span><i className="bi bi-person-badge me-1" />Nomine e figure</span>}>
                {editing.id_azienda ? (
                    <NominePanel idAzienda={editing.id_azienda} />
                ) : (
                    <BsAlert variant="info" className="py-2 mb-0">
                        <i className="bi bi-info-circle me-1" />
                        Salva prima i dati dell'azienda per gestire RSPP, medico competente, RLS e altre figure della sicurezza.
                    </BsAlert>
                )}
            </Tab>

            <Tab eventKey="licenze" title={<span><i className="bi bi-award me-1" />Autorizzazioni e titoli</span>}>
                {editing.id_azienda ? (
                    <LicenzePanel idAzienda={editing.id_azienda} settore={editing.settori_codici || editing.settore_codice} />
                ) : (
                    <BsAlert variant="info" className="py-2 mb-0">
                        <i className="bi bi-info-circle me-1" />
                        Salva prima i dati dell'azienda per gestire autorizzazioni, polizze, regolamenti e certificazioni aziendali.
                    </BsAlert>
                )}
            </Tab>

            <Tab eventKey="valutazioni" title={<span><i className="bi bi-clipboard-data me-1" />Valutazioni</span>}>
                {editing.id_azienda ? (
                    <ValutazioniPanel idAzienda={editing.id_azienda} settore={editing.settori_codici || editing.settore_codice} />
                ) : (
                    <BsAlert variant="info" className="py-2 mb-0">
                        <i className="bi bi-info-circle me-1" />
                        Salva prima i dati dell'azienda per gestire le valutazioni dei rischi specifiche.
                    </BsAlert>
                )}
            </Tab>

            <Tab eventKey="appalti" title={<span><i className="bi bi-briefcase me-1" />Appalti</span>}>
                {editing.id_azienda ? (
                    <AppaltiPanel idAzienda={editing.id_azienda} />
                ) : (
                    <BsAlert variant="info" className="py-2 mb-0">
                        <i className="bi bi-info-circle me-1" />
                        Salva prima i dati dell'azienda per gestire appalti e idoneità di imprese esterne.
                    </BsAlert>
                )}
            </Tab>

            <Tab eventKey="infortuni" title={<span><i className="bi bi-bandaid me-1" />Infortuni</span>}>
                {editing.id_azienda ? (
                    <InfortuniPanel idAzienda={editing.id_azienda} />
                ) : (
                    <BsAlert variant="info" className="py-2 mb-0">
                        <i className="bi bi-info-circle me-1" />
                        Salva prima i dati dell'azienda per gestire il registro infortuni e near miss.
                    </BsAlert>
                )}
            </Tab>

            <Tab eventKey="riunioni" title={<span><i className="bi bi-calendar-check me-1" />Riunioni</span>}>
                {editing.id_azienda ? (
                    <RiunioniPanel idAzienda={editing.id_azienda} numeroDipendenti={editing.numero_dipendenti} />
                ) : (
                    <BsAlert variant="info" className="py-2 mb-0">
                        <i className="bi bi-info-circle me-1" />
                        Salva prima i dati dell'azienda per gestire le riunioni periodiche (art. 35).
                    </BsAlert>
                )}
            </Tab>

            <Tab eventKey="sopralluoghi" title={<span><i className="bi bi-search me-1" />Sopralluoghi</span>}>
                {editing.id_azienda ? (
                    <SopralluoghiPanel idAzienda={editing.id_azienda} />
                ) : (
                    <BsAlert variant="info" className="py-2 mb-0">
                        <i className="bi bi-info-circle me-1" />
                        Salva prima i dati dell'azienda per gestire sopralluoghi e non conformità.
                    </BsAlert>
                )}
            </Tab>

            <Tab eventKey="dpi" title={<span><i className="bi bi-shield me-1" />DPI</span>}>
                {editing.id_azienda ? (
                    <DpiPanel idAzienda={editing.id_azienda} settore={editing.settori_codici || editing.settore_codice} />
                ) : (
                    <BsAlert variant="info" className="py-2 mb-0">
                        <i className="bi bi-info-circle me-1" />
                        Salva prima i dati dell'azienda per gestire DPI per mansione e registro consegne.
                    </BsAlert>
                )}
            </Tab>

            <Tab eventKey="particolari" title={<span><i className="bi bi-person-hearts me-1" />Particolari</span>}>
                {editing.id_azienda ? (
                    <ParticolariPanel idAzienda={editing.id_azienda} settore={editing.settori_codici || editing.settore_codice} />
                ) : (
                    <BsAlert variant="info" className="py-2 mb-0">
                        <i className="bi bi-info-circle me-1" />
                        Salva prima i dati dell'azienda per gestire lavoratori in condizioni particolari.
                    </BsAlert>
                )}
            </Tab>

            <Tab eventKey="ispettiva" title={<span><i className="bi bi-folder-symlink me-1" />Ispettiva</span>}>
                {editing.id_azienda ? (
                    <IspettivaPanel idAzienda={editing.id_azienda} settore={editing.settori_codici || editing.settore_codice} />
                ) : (
                    <BsAlert variant="info" className="py-2 mb-0">
                        <i className="bi bi-info-circle me-1" />
                        Salva prima i dati dell'azienda per gestire checklist pronto-verifica e verbali ispettivi.
                    </BsAlert>
                )}
            </Tab>

            <Tab eventKey="dotazioni" title={<span><i className="bi bi-shield-shaded me-1" />Dotazioni</span>}>
                {editing.id_azienda ? (
                    <DotazioniPanel idAzienda={editing.id_azienda} settore={editing.settori_codici || editing.settore_codice} />
                ) : (
                    <BsAlert variant="info" className="py-2 mb-0">
                        <i className="bi bi-info-circle me-1" />
                        Salva prima i dati dell'azienda per gestire il registro dotazioni e armamento.
                    </BsAlert>
                )}
            </Tab>

            <Tab eventKey="attrezzature" title={<span><i className="bi bi-tools me-1" />Attrezzature</span>}>
                {editing.id_azienda ? (
                    <AttrezzaturePanel idAzienda={editing.id_azienda} settore={editing.settori_codici || editing.settore_codice} />
                ) : (
                    <BsAlert variant="info" className="py-2 mb-0">
                        <i className="bi bi-info-circle me-1" />
                        Salva prima i dati dell'azienda per gestire attrezzature e verifiche periodiche.
                    </BsAlert>
                )}
            </Tab>
        </Tabs>
    );
}
